import { useState } from 'react';
import styled from 'styled-components';

import { dropTargetHighlight } from './dragStyles';
import FolderContextMenu from './FolderContextMenu';
import { useStoryDropTarget } from './useStoryDropTarget';

import type { FolderAggregate } from './folderAggregates';
import type { Folder } from '@src/types.ts/story';
import type { FC, MouseEvent } from 'react';

type Props = {
  folder: Folder;
  aggregate?: FolderAggregate;
  previewImageUrl?: string;
  onOpen: (folderId: string) => void;
  onCopyLink: (folder: Folder) => void;
  onRename: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onStoriesDropped?: (storyIds: string[], folderId: string | null) => void;
  // Suppress the rename/delete context menu — used by the virtual "Unassigned"
  // tile, which isn't a real, editable folder.
  hideMenu?: boolean;
};

const FolderCard: FC<Props> = ({
  folder,
  aggregate,
  previewImageUrl,
  onOpen,
  onCopyLink,
  onRename,
  onDelete,
  onStoriesDropped,
  hideMenu,
}) => {
  const [contextMenuPos, setContextMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const { isDropTarget, dropHandlers } = useStoryDropTarget(
    folder.id,
    onStoriesDropped,
  );

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
  };

  const stats: string[] = [];
  if (aggregate?.storyCount) {
    stats.push(
      `${aggregate.storyCount} ${aggregate.storyCount === 1 ? 'story' : 'stories'}`,
    );
  }
  if (aggregate?.clipCount) {
    stats.push(
      `${aggregate.clipCount} ${aggregate.clipCount === 1 ? 'clip' : 'clips'}`,
    );
  }

  return (
    <Card
      onClick={() => onOpen(folder.id)}
      onContextMenu={hideMenu ? undefined : handleContextMenu}
      {...dropHandlers}
      data-drop-target={isDropTarget || undefined}
      data-testid={`folder-card-${folder.id}`}
    >
      <FolderThumb
        data-empty={!previewImageUrl || undefined}
        aria-hidden="true"
      >
        <FolderTab />
        <FolderBody>
          {previewImageUrl && <Preview src={previewImageUrl} alt="" />}
        </FolderBody>
      </FolderThumb>
      <Body>
        <CardName title={folder.name}>{folder.name}</CardName>
        {stats.length > 0 && <CardStats>{stats.join(' · ')}</CardStats>}
      </Body>
      {!hideMenu && (
        <FolderContextMenu
          onCopyLink={() => onCopyLink(folder)}
          onRename={() => onRename(folder)}
          onDelete={() => onDelete(folder)}
          contextMenuPos={contextMenuPos}
          onCloseContextMenu={() => setContextMenuPos(null)}
        />
      )}
    </Card>
  );
};

export default FolderCard;

const Card = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
  overflow: hidden;
  background: var(--surface);
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background 120ms ease,
    box-shadow 120ms ease;

  &:hover {
    border-color: var(--text-muted);
    background: var(--surface-elevated);
  }

  &[data-drop-target] {
    border-color: var(--primary);
    ${dropTargetHighlight}
  }
`;

/* Illustrative folder graphic: a tabbed folder silhouette that frames the
   preview image inside its body, so a folder is instantly distinguishable from
   a story/clip thumbnail. Empty folders show the bare silhouette. */
const FOLDER_FILL = 'linear-gradient(155deg, var(--gray-200), var(--gray-300))';

const FolderThumb = styled.div`
  position: relative;
  width: 62px;
  height: 50px;
  flex-shrink: 0;
`;

// The little tab on the top-left; merges with the body into one silhouette.
const FolderTab = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 24px;
  height: 12px;
  background: var(--gray-300);
  border-radius: 5px 5px 0 0;
`;

const FolderBody = styled.div`
  position: absolute;
  inset: 8px 0 0 0;
  background: ${FOLDER_FILL};
  /* Square top-left so it reads as continuous with the tab; rounded elsewhere. */
  border-radius: 2px 7px 7px 7px;
  overflow: hidden;
  /* Subtle top lip + depth so the folder doesn't read flat. */
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 1px 2px rgba(0, 0, 0, 0.25);
`;

// Preview tucked inside the folder body, framed by a thin folder-colored border.
const Preview = styled.img`
  position: absolute;
  inset: 4px;
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  border-radius: 3px;
  object-fit: cover;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const CardName = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardStats = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  /* Single line like CardName — wrapping made narrow cards taller than their
     siblings (cards must stay equal height across the grid). */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
